import { NoImage } from "@assets/constant/index.constant";
import { Button } from "@components/ui/button";
import { UseMutateFunction } from "@tanstack/react-query";
import Tooltip from "@ui/external/components/tooltip";
import { ZoomImage } from "@ui/internal/components/formelement";
import { formatDateTime, isNullCheck } from "@utils/helper/index.helper";
import { useHandleAction } from "@utils/hook/index.hook";
import { useSelectionStore } from "@utils/states/index.state";
import { AxiosResponse } from "axios";
import { EyeIcon, EyeOffIcon } from "lucide-react";
import { useState } from "react";

interface CloumnListProps {
  resendEmail: UseMutateFunction<
    AxiosResponse<any, any>,
    Error,
    { id: any },
    unknown
  >;
  resendEmailPending: boolean;
}

const PasswordCell = ({ row }: { row: any }) => {
  const [showPassword, setShowPassword] = useState(false);
  const password = row?.teams_password;
  const togglePassword = () => setShowPassword((prev) => !prev);
  return (
    <div className="flex items-center space-x-2 text-lg font-semibold text-gray-800">
      <Tooltip description={password}>
        <span className="max-w-[120px] cursor-pointer capitalize block truncate">
          {showPassword ? (isNullCheck(password) as string) : "••••••••"}
        </span>
      </Tooltip>
      <button
        onClick={togglePassword}
        className="focus:outline-none"
        aria-label="Toggle password visibility"
      >
        {showPassword ? (
          <EyeIcon className="w-5 h-5 text-gray-600" />
        ) : (
          <EyeOffIcon className="w-5 h-5 text-gray-600" />
        )}
      </button>
    </div>
  );
};

const ResendEmailCell = ({
  row,
  resendEmail,
  resendEmailPending,
}: {
  row: any;
  resendEmail: UseMutateFunction<AxiosResponse<any, any>, Error, { id: any }, unknown>;
  resendEmailPending: boolean;
}) => {
  const [isPendingId, setIsPendingId] = useState("");
  return (
    <Button
      variant="defaultoutline"
      className=" cursor-pointer text-[#1DA077]"
      disabled={resendEmailPending && isPendingId === row.id}
      onClick={() => {
        resendEmail({ id: row.id });
        setIsPendingId(row.id);
      }}
    >
      Resend Email
    </Button>
  );
};

const ColumnList = ({ resendEmail, resendEmailPending }: CloumnListProps) => {
  const { handleAction } = useHandleAction({});
  const { clearSelection } = useSelectionStore();
  return [
    {
      name: "ID",

      cell: (row: any) => (
        <span className="font-semibold text-gray-700">{row?.id}</span>
      ),
      sortable: true,
    },
    {
      name: "Created Date",
      accessKey: "created_date",
      cell: (row: any) => {
        return (
          <span className="font-semibold text-gray-700">
            {row.updated_at
              ? formatDateTime(row?.updated_at)
              : formatDateTime(row?.created_at)}
          </span>
        );
      },
      sortable: false,
    },
    {
      name: "Image",

      cell: (row: any) => (
        <div className="flex items-center gap-3">
          <ZoomImage>
            <img
              src={row?.student?.user?.professional_image || NoImage}
              alt="featured"
              onError={(e: any) => {
                const target = e.target as HTMLImageElement;
                target.onerror = null;
                target.src = NoImage;
              }}
              className="w-12 h-12 object-cover rounded"
            />
          </ZoomImage>
          <Tooltip description={row?.student?.user?.full_name}>
            <span className="max-w-[120px] cursor-pointer capitalize block truncate">
              {isNullCheck(row?.student?.user?.full_name) as string}
            </span>
          </Tooltip>
        </div>
      ),
      sortable: false,
    },

    {
      name: "Email",
      cell: (row: any) => (
        <div className="text-lg capitalize font-semibold text-gray-800">
          <Tooltip description={row?.student?.user?.email}>
            <span className="max-w-[120px] cursor-pointer capitalize block truncate">
              {isNullCheck(row?.student?.user?.email) as string}
            </span>
          </Tooltip>
        </div>
      ),
      sortable: true,
    },

    {
      name: "Teams Email",
      cell: (row: any) => (
        <div className="text-lg capitalize font-semibold text-gray-800">
          <Tooltip description={row?.teams_email}>
            <span className="max-w-[120px] cursor-pointer capitalize block truncate">
              {isNullCheck(row?.teams_email) as string}
            </span>
          </Tooltip>
        </div>
      ),
      sortable: true,
    },

    {
      name: "Password",
      cell: (row: any) => <PasswordCell row={row} />,
      sortable: true,
    },
    {
      name: "Latest Email Sent Date",
      accessKey: "latest_email_sent_at",
      cell: (row: any) => {
        return (
          <span className="font-semibold text-gray-700">
            {row.latest_email_sent_at
              ? formatDateTime(row.latest_email_sent_at)
              : ""}
          </span>
        );
      },
      sortable: true,
    },
    {
      name: "Resend Email",
      cell: (row: any) => (
        <ResendEmailCell
          row={row}
          resendEmail={resendEmail}
          resendEmailPending={resendEmailPending}
        />
      ),
      sortable: true,
    },
    {
      name: "Actions",
      cell: (row: any) => (
        <div className="flex items-center gap-3  cursor-pointer">
          <button
            className=" cursor-pointer text-[#1DA077]"
            onClick={() => handleAction({ type: "modal-edit", data: row.id })}
          >
            Edit
          </button>
          <button
            className=" cursor-pointer text-[#C33241]"
            onClick={() => {
              clearSelection();
              handleAction({
                type: "confirm",
                data: { type: "delete", id: row.id },
              });
            }}
          >
            Delete
          </button>
        </div>
      ),
      sortable: false,
    },
  ];
};

export { ColumnList };
